import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const TAB_HEIGHT = 116;
const EDGE_GAP = 12;
const DRAG_THRESHOLD = 5;
// Matches the "Post an Ad" FAB (src/tenant/common/components/layout/styled.js): 8vmin offset,
// AntD controlHeightLG diameter — so the dragged tab is clamped clear of it in the member area.
const FAB_DIAMETER = 40;
const DEFAULT_Y = 0.45;

const clampToUnit = (v) => Math.min(1, Math.max(0, v));

const readStoredY = (storageKey) => {
  if (!storageKey) return DEFAULT_Y;
  try {
    const parsed = parseFloat(localStorage.getItem(storageKey));
    if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 1) return parsed;
  } catch {
    // localStorage unavailable (private mode, etc.) — fall back to the default.
  }
  return DEFAULT_Y;
};

// Vertical-only drag for the FEEDBACK tab. Fixes three defects present in the reference design's
// own drag implementation (see plan T6): a grabOffset so the tab doesn't snap its centre under the
// pointer on grab, a movement threshold so a tap isn't swallowed by 1px of jitter, and a
// pointercancel handler so an interrupted touch gesture can't strand the tab mid-drag forever.
const useFeedbackTabPosition = ({ userId, isMemberArea, isBannerVisible, headerHeight, fabVisible }) => {
  const storageKey = userId ? `profolioFeedbackTabY_${userId}` : null;

  const [y, setY] = useState(() => readStoredY(storageKey));
  const [dragging, setDragging] = useState(false);
  const [moved, setMoved] = useState(false);

  const grabOffset = useRef(0);
  const startY = useRef(0);
  const activePointerId = useRef(null);
  const boundsRef = useRef({ minY: 0, maxY: 1 });

  const computeBounds = useCallback(() => {
    const half = TAB_HEIGHT / 2;
    const vmin8 = 0.08 * Math.min(window.innerWidth, window.innerHeight);
    const topPx = isMemberArea ? EDGE_GAP : (headerHeight || 0) + (isBannerVisible ? 66 : 0);
    const bottomPx = isMemberArea && fabVisible ? Math.max(EDGE_GAP, vmin8 + FAB_DIAMETER + EDGE_GAP) : EDGE_GAP;
    boundsRef.current = {
      minY: (topPx + half) / window.innerHeight,
      maxY: 1 - (bottomPx + half) / window.innerHeight,
    };
  }, [isMemberArea, isBannerVisible, headerHeight, fabVisible]);

  const clamp = useCallback((v) => {
    const { minY, maxY } = boundsRef.current;
    return Math.min(maxY, Math.max(minY, clampToUnit(v)));
  }, []);

  // Recompute and re-clamp immediately whenever anything the bounds depend on changes (resize,
  // banner toggling, header height, member-area FAB visibility) so a shrunk viewport can never
  // strand the tab off-screen.
  useLayoutEffect(() => {
    computeBounds();
    setY((prev) => clamp(prev));
  }, [computeBounds, clamp]);

  useEffect(() => {
    const onResize = () => {
      computeBounds();
      setY((prev) => clamp(prev));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [computeBounds, clamp]);

  const persist = useCallback(
    (value) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, String(value));
      } catch {
        // Ignore write failures (private mode / quota) — the in-memory position still applies.
      }
    },
    [storageKey],
  );

  const endDrag = useCallback(() => {
    setDragging(false);
    activePointerId.current = null;
    setY((current) => {
      persist(current);
      return current;
    });
  }, [persist]);

  // A second finger touching mid-drag must not hijack it — ignore extra pointerdowns while one
  // pointer is already active.
  const onPointerDown = useCallback(
    (e) => {
      if (activePointerId.current !== null) return;
      e.preventDefault();
      if (e.currentTarget.setPointerCapture) {
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // Some environments (jsdom, older browsers) don't support capture — drag still works
          // via the window-level nature of pointer events once dragging state is set.
        }
      }
      activePointerId.current = e.pointerId;
      grabOffset.current = e.clientY - y * window.innerHeight;
      startY.current = e.clientY;
      setDragging(true);
      setMoved(false);
    },
    [y],
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!dragging || e.pointerId !== activePointerId.current) return;
      if (Math.abs(e.clientY - startY.current) > DRAG_THRESHOLD) setMoved(true);
      setY(clamp((e.clientY - grabOffset.current) / window.innerHeight));
    },
    [dragging, clamp],
  );

  const onPointerUp = useCallback(
    (e) => {
      if (activePointerId.current !== null && e.pointerId !== activePointerId.current) return;
      endDrag();
    },
    [endDrag],
  );

  const onPointerCancel = useCallback(
    (e) => {
      if (activePointerId.current !== null && e.pointerId !== activePointerId.current) return;
      endDrag();
    },
    [endDrag],
  );

  return {
    y,
    dragging,
    moved,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
  };
};

export default useFeedbackTabPosition;
